import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Car, Clock, MapPin, Loader2, AlertCircle, Home as HomeIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const isoLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const shiftDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return isoLocal(d);
};

const fmtMinutes = (seconds) => {
  if (!seconds) return "0 min";
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m} min`;
};

const fmtDateLabel = (ds) => {
  const [y, m, d] = ds.split("-").map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
};

const PRESETS = [
  { label: "Today", offset: 0, span: 0 },
  { label: "Last 7 days", offset: -6, span: 6 },
  { label: "Last 30 days", offset: -29, span: 29 },
  { label: "This month", month: true },
];

export default function DriveTime() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(shiftDays(-6));
  const [endDate, setEndDate] = useState(shiftDays(0));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({}); // tech_id → boolean
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  const runProbe = async () => {
    setProbing(true);
    setProbeResult(null);
    try {
      const token = getSessionToken();
      const { data } = await axios.get(`${API}/reports/drive-time/probe`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProbeResult(data);
      if (data?.ok) {
        toast.success("Routes API is working — tap Refresh.");
      }
    } catch (e) {
      setProbeResult({
        ok: false,
        google_message: e?.response?.data?.detail || e?.message || "Probe failed",
        hint: "Backend couldn't reach Google. Check network / proxy.",
      });
    } finally {
      setProbing(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.get(`${API}/reports/drive-time`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: endDate },
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load drive time data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p) => {
    if (p.month) {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(isoLocal(first));
      setEndDate(isoLocal(now));
    } else {
      setStartDate(shiftDays(p.offset));
      setEndDate(shiftDays(0));
    }
  };

  const grand = data?.grand_total || { miles: 0, drive_seconds: 0, jobs: 0 };
  const techs = data?.techs || [];

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate("/settings")}
            data-testid="drive-time-back"
            className="p-2 hover:bg-zinc-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-barlow text-xl font-bold text-zinc-950">Drive Time</h1>
            <p className="text-xs text-zinc-500">Per-tech miles + drive time from Home → Jobs → Home</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="drive-time-refresh"
            className="p-2 hover:bg-zinc-100 rounded-lg disabled:opacity-50"
            title="Refresh"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-5">
        {/* Routes API disabled — most common root cause of missing miles.
            One-click deep link to the exact enablement page for the admin. */}
        {data?.routes_api_disabled && (
          <div
            className="rounded-xl border-2 border-red-300 bg-red-50 p-4"
            data-testid="routes-api-disabled-banner"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1 min-w-0">
                <p className="font-bold text-red-900">Google is rejecting the Routes API call</p>
                <p className="text-red-800 mt-1">
                  Three different causes all look like this. Click
                  &quot;Diagnose&quot; below to see Google&apos;s exact response —
                  it will tell us which one applies.
                </p>
                {data.routes_api_error_message && (
                  <div
                    className="mt-2 bg-white rounded border border-red-200 p-2 font-mono text-[11px] text-red-900 break-words"
                    data-testid="routes-api-error-raw"
                  >
                    {data.routes_api_error_message}
                  </div>
                )}
                <ul className="list-disc list-inside text-red-800 mt-2 space-y-1 text-xs">
                  <li>
                    Routes API not enabled on the project —{" "}
                    <a
                      href="https://console.cloud.google.com/apis/library/routes.googleapis.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      enable it here
                    </a>
                  </li>
                  <li>
                    API key has API restrictions and Routes API isn&apos;t
                    in the allowed list —{" "}
                    <a
                      href="https://console.cloud.google.com/apis/credentials"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      edit the key
                    </a>
                    , open &quot;API restrictions&quot; and add Routes API
                  </li>
                  <li>
                    Billing not enabled on the project —{" "}
                    <a
                      href="https://console.cloud.google.com/billing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      turn it on
                    </a>
                  </li>
                </ul>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={runProbe}
                    disabled={probing}
                    data-testid="routes-api-diagnose-btn"
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-700 hover:bg-red-800 text-white disabled:opacity-50"
                  >
                    {probing ? "Diagnosing…" : "Diagnose"}
                  </button>
                  <button
                    type="button"
                    onClick={fetchData}
                    disabled={loading}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-red-300 text-red-900 hover:bg-red-100 disabled:opacity-50"
                  >
                    Refresh
                  </button>
                </div>
                {probeResult && (
                  <div
                    className="mt-3 bg-white rounded border border-red-200 p-3 text-xs"
                    data-testid="routes-api-probe-result"
                  >
                    <p className="font-semibold text-red-900">
                      {probeResult.ok
                        ? "✓ Routes API is working now"
                        : "What Google said:"}
                    </p>
                    {!probeResult.ok && (
                      <>
                        <p className="mt-1 text-red-800">
                          <span className="font-medium">Status:</span>{" "}
                          {probeResult.google_status || `HTTP ${probeResult.http_status || "?"}`}
                        </p>
                        {probeResult.google_message && (
                          <p className="mt-1 text-red-800 font-mono break-words">
                            {probeResult.google_message}
                          </p>
                        )}
                        {probeResult.hint && (
                          <p className="mt-2 text-red-900 font-medium">
                            → {probeResult.hint}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
                <p className="text-red-800 mt-2 text-[11px]">
                  Costs about $0.005 per lookup. Every leg is cached for 30 days so a
                  typical daily report is under a penny after the first run.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Date range controls */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyPreset(p)}
                data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 py-1.5 text-xs font-medium rounded-full bg-zinc-100 hover:bg-[#207024] hover:text-white transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-zinc-500 mb-1">Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="drive-time-start"
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-zinc-500 mb-1">End date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="drive-time-end"
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm"
              />
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              data-testid="drive-time-apply"
              className="px-4 py-2 bg-[#207024] text-white rounded-lg text-sm font-semibold hover:bg-[#185519] disabled:opacity-60"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Grand total KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border p-4" data-testid="kpi-total-miles">
            <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider mb-1">
              <Car className="w-3.5 h-3.5" /> Total miles
            </div>
            <div className="text-2xl font-bold text-zinc-950">
              {grand.miles.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="text-[11px] text-zinc-400">across {techs.length} tech{techs.length === 1 ? "" : "s"}</div>
          </div>
          <div className="bg-white rounded-xl border p-4" data-testid="kpi-total-time">
            <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider mb-1">
              <Clock className="w-3.5 h-3.5" /> Drive time
            </div>
            <div className="text-2xl font-bold text-zinc-950">
              {fmtMinutes(grand.drive_seconds)}
            </div>
            <div className="text-[11px] text-zinc-400">windshield-time only</div>
          </div>
          <div className="bg-white rounded-xl border p-4 col-span-2 md:col-span-1" data-testid="kpi-total-jobs">
            <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider mb-1">
              <MapPin className="w-3.5 h-3.5" /> Jobs completed
            </div>
            <div className="text-2xl font-bold text-zinc-950">{grand.jobs}</div>
            <div className="text-[11px] text-zinc-400">excludes cancelled</div>
          </div>
        </div>

        {/* Per-tech breakdown */}
        {loading && !data && (
          <div className="text-center py-12 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            <p className="text-sm">Loading drive time…</p>
          </div>
        )}

        {!loading && techs.length === 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-8 text-center">
            <MapPin className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-600">No jobs found in this date range.</p>
          </div>
        )}

        {techs.map((tech) => (
          <div
            key={tech.tech_id}
            className="bg-white rounded-xl border border-zinc-200 overflow-hidden"
            data-testid={`tech-card-${tech.tech_id}`}
          >
            <div className="p-4 border-b border-zinc-100">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-semibold text-zinc-950 text-lg">{tech.tech_name}</h3>
                  <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-0.5">
                    <HomeIcon className="w-3.5 h-3.5" />
                    {tech.home_base || <span className="italic text-amber-600">No home base set</span>}
                  </div>
                </div>
                <button
                  onClick={() => setExpanded((s) => ({ ...s, [tech.tech_id]: !s[tech.tech_id] }))}
                  data-testid={`expand-${tech.tech_id}`}
                  className="text-xs font-semibold text-[#207024] hover:underline"
                >
                  {expanded[tech.tech_id] ? "Hide day-by-day" : "Show day-by-day"}
                </button>
              </div>

              {tech.warning && (
                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{tech.warning}</span>
                </div>
              )}

              {/* Per-tech KPI grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Miles</div>
                  <div className="text-lg font-bold text-zinc-950">
                    {tech.total_miles.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Drive time</div>
                  <div className="text-lg font-bold text-zinc-950">{fmtMinutes(tech.total_drive_seconds)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">% of workday</div>
                  <div className={`text-lg font-bold ${tech.drive_percent_of_workday > 40 ? "text-red-600" : "text-zinc-950"}`}>
                    {tech.drive_percent_of_workday || 0}%
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Jobs</div>
                  <div className="text-lg font-bold text-zinc-950">{tech.total_jobs}</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Avg mi/job</div>
                  <div className="text-lg font-bold text-zinc-950">{tech.avg_miles_per_job || 0}</div>
                </div>
              </div>
            </div>

            {/* Day-by-day breakdown */}
            {expanded[tech.tech_id] && (
              <div className="bg-zinc-50 p-4 space-y-3" data-testid={`days-${tech.tech_id}`}>
                {tech.days.length === 0 ? (
                  <p className="text-xs text-zinc-500 text-center py-2">No jobs in range for this tech.</p>
                ) : (
                  tech.days.map((day) => (
                    <div key={day.date} className="bg-white rounded-lg border border-zinc-200 p-3">
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                        <div className="font-semibold text-zinc-900 text-sm">{fmtDateLabel(day.date)}</div>
                        <div className="flex gap-3 text-xs text-zinc-600">
                          <span><strong>{day.total_miles || 0}</strong> mi</span>
                          <span><strong>{fmtMinutes(day.total_drive_seconds)}</strong></span>
                          <span>{day.job_count} job{day.job_count === 1 ? "" : "s"}</span>
                        </div>
                      </div>
                      {day.warning && (
                        <p className="text-[11px] text-amber-700 mb-2 flex items-start gap-1">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>{day.warning}</span>
                        </p>
                      )}
                      {day.legs && day.legs.length > 0 && (
                        <div className="space-y-1 text-xs">
                          {day.legs.map((leg, i) => (
                            <div key={i} className="flex items-center justify-between text-zinc-600 py-0.5">
                              <span className="truncate">
                                <span className="text-zinc-400">{i + 1}.</span> {leg.from_label} → {leg.to_label}
                              </span>
                              <span className="font-medium text-zinc-800 whitespace-nowrap ml-2">
                                {leg.miles} mi · {fmtMinutes(leg.seconds)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
